import { OrthographicCamera } from 'three';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { cuboidVertexSizePx } from '../../../render/mesh/cuboid/cuboid-vertices';
import { ViewRegion } from '../../../state/view-region-state';
import { NdcBoundingBox, isPointInsideBox } from './drag-handler-utils';

export function getEdgeTolerance(
  viewRegion: ViewRegion,
  camera: OrthographicCamera,
) {
  const [width] = viewRegion.size;
  const { left, right } = camera;
  const worldUnitPerPixel = ((right - left) / width) * camera.zoom;

  const worldUnits = cuboidVertexSizePx * worldUnitPerPixel;
  const ndcUnits = ((worldUnits - left) / (right - left)) * 2 - 1;
  return ndcUnits / 2;
}

export function isOverEdge(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  return (
    isPointWithinLeftEdge(containerPoint, boundingBox, tolerance) ||
    isPointWithinRightEdge(containerPoint, boundingBox, tolerance) ||
    isPointWithinTopEdge(containerPoint, boundingBox, tolerance) ||
    isPointWithinBottomEdge(containerPoint, boundingBox, tolerance)
  );
}

export function isOverCorner(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  return (
    isPointWithinTopLeftCorner(containerPoint, boundingBox, tolerance) ||
    isPointWithinTopRightCorner(containerPoint, boundingBox, tolerance) ||
    isPointWithinBottomRightCorner(containerPoint, boundingBox, tolerance) ||
    isPointWithinBottomLeftCorner(containerPoint, boundingBox, tolerance)
  );
}

export function isPointWithinTopLeftCorner(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const corner = [boundingBox.min[0], boundingBox.max[1]];
  return isPointInsideBox(containerPoint, {
    min: [corner[0] - tolerance, corner[1] - tolerance],
    max: [corner[0] + tolerance, corner[1] + tolerance],
  });
}

export function isPointWithinTopRightCorner(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const corner = [boundingBox.max[0], boundingBox.max[1]];
  return isPointInsideBox(containerPoint, {
    min: [corner[0] - tolerance, corner[1] - tolerance],
    max: [corner[0] + tolerance, corner[1] + tolerance],
  });
}

export function isPointWithinBottomLeftCorner(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const corner = [boundingBox.min[0], boundingBox.min[1]];
  return isPointInsideBox(containerPoint, {
    min: [corner[0] - tolerance, corner[1] - tolerance],
    max: [corner[0] + tolerance, corner[1] + tolerance],
  });
}

export function isPointWithinBottomRightCorner(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const corner = [boundingBox.max[0], boundingBox.min[1]];
  return isPointInsideBox(containerPoint, {
    min: [corner[0] - tolerance, corner[1] - tolerance],
    max: [corner[0] + tolerance, corner[1] + tolerance],
  });
}

export function isPointWithinLeftEdge(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const { min, max } = boundingBox;
  return isPointInsideBox(containerPoint, {
    min: [min[0] - tolerance, min[1]],
    max: [min[0] + tolerance, max[1]],
  });
}

export function isPointWithinRightEdge(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const { min, max } = boundingBox;
  return isPointInsideBox(containerPoint, {
    min: [max[0] - tolerance, min[1]],
    max: [max[0] + tolerance, max[1]],
  });
}

export function isPointWithinTopEdge(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const { min, max } = boundingBox;
  return isPointInsideBox(containerPoint, {
    min: [min[0], max[1] - tolerance],
    max: [max[0], max[1] + tolerance],
  });
}

export function isPointWithinBottomEdge(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  tolerance: number,
) {
  const { min, max } = boundingBox;
  return isPointInsideBox(containerPoint, {
    min: [min[0], min[1] - tolerance],
    max: [max[0], min[1] + tolerance],
  });
}
